import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, Search, User, ChevronRight, Calendar, Settings, LogOut, ChevronDown, Mail, Menu } from 'lucide-react';
import NotificationBell from '../NotificationBell';
import InternalMessages from '../InternalMessages';

// Servizi dropdown structure
const SERVIZI_DROPDOWN = {
  clinica: {
    label: 'Clinica',
    items: [
      { label: 'Ambliopia', path: '/specialization/amblyopia' },
      { label: 'Astigmatismo', path: '/specialization/astigmatism' },
      { label: 'Calazio', path: '/specialization/chalazion' },
      { label: 'Cheratiti Infettive', path: '/specialization/keratitis' },
      { label: 'Cheratocono', path: '/specialization/keratoconus' },
      { label: 'Congiuntiviti', path: '/specialization/conjunctivitis' },
      { label: 'Glaucoma', path: '/specialization/glaucoma' },
      { label: 'Ipermetropia', path: '/specialization/hypermetropia' },
      { label: 'Maculopatia', path: '/specialization/maculopathy' },
      { label: 'Miopia', path: '/specialization/miopia' },
      { label: 'Occhio Secco', path: '/specialization/dry_eye' },
      { label: 'Presbiopia', path: '/specialization/presbyopia' },
      { label: 'Ptosi Palpebrale', path: '/specialization/ptosis' },
      { label: 'Retinopatia Diabetica', path: '/specialization/diabetic_retinopathy' },
      { label: 'Strabismo', path: '/specialization/strabismus' },
      { label: 'Vista Pediatrica', path: '/specialization/pediatric_vision' },
      { label: 'Vizi di Refrazione', path: '/specialization/refractive_errors' },
    ]
  },
  chirurgia: {
    label: 'Chirurgia',
    items: [
      { label: 'Blefaroplastica', path: '/specialization/blepharoplasty' },
      { label: 'Chirurgia della Cataratta', path: '/specialization/cataract' },
      { label: 'Chirurgia del Pterigio', path: '/specialization/pterygium' },
      { label: 'Chirurgia dello Strabismo', path: '/specialization/strabismus' },
      { label: 'Chirurgia Refrattiva', path: '/specialization/refractive_surgery' },
    ]
  }
};

// Navigation links
const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Chi Siamo', path: '/chi-siamo' },
  { label: 'Équipe Medica', path: '/equipe' },
  { label: 'Servizi', path: null, hasDropdown: true },
  { label: 'Prenota Visita', path: '/booking' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contatti', path: '/contatti' },
  { label: 'Lavora con noi', path: '/lavora-con-noi' },
];

export const Navbar = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServiziOpen, setIsServiziOpen] = useState(false);
  const [mobileServiziOpen, setMobileServiziOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [user, setUser] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const serviziRef = useRef(null);
  const serviziTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Transparent navbar only on homepage when not scrolled
  const isHomePage = location.pathname === '/';
  const isTransparent = isHomePage && !isScrolled;

  const searchablePages = [
    { title: 'Home', path: '/', keywords: ['home', 'principale', 'inizio'] },
    { title: 'Chi Siamo', path: '/chi-siamo', keywords: ['chi siamo', 'about', 'clinica', 'storia', 'prime vision'] },
    { title: 'Équipe Medica', path: '/equipe', keywords: ['equipe', 'medici', 'dottori', 'staff', 'oculista', 'ortottista'] },
    { title: 'Prenota Visita', path: '/booking', keywords: ['prenota', 'appuntamento', 'visita', 'prenotazione', 'calendario'] },
    { title: 'Contatti', path: '/contatti', keywords: ['contatti', 'telefono', 'email', 'indirizzo', 'dove siamo', 'mappa'] },
    { title: 'Abbonamenti', path: '/abbonamenti', keywords: ['abbonamento', 'abbonamenti', 'piano', 'sottoscrizione'] },
    { title: 'Blog Oftalmologico', path: '/blog', keywords: ['blog', 'articoli', 'approfondimenti', 'salute occhi'] },
    { title: 'Lavora con noi', path: '/lavora-con-noi', keywords: ['lavora', 'lavoro', 'carriera', 'candidatura'] },
    { title: 'Test Visivi', path: '/tests', keywords: ['test', 'autovalutazione', 'controllo', 'vista'] },
    { title: 'Cataratta', path: '/specialization/cataratta', keywords: ['cataratta', 'cristallino'] },
    { title: 'Glaucoma', path: '/specialization/glaucoma', keywords: ['glaucoma', 'pressione oculare'] },
    { title: 'Strabismo', path: '/specialization/strabismus', keywords: ['strabismo', 'occhi storti'] },
    { title: 'Privacy Policy', path: '/privacy', keywords: ['privacy', 'dati', 'gdpr'] },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close servizi dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (serviziRef.current && !serviziRef.current.contains(e.target)) {
        setIsServiziOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      const results = searchablePages.filter(page => {
        if (page.requiresAuth && !user) return false;
        return page.title.toLowerCase().includes(query) ||
               page.keywords.some(kw => kw.includes(query));
      });
      setSearchResults(results.slice(0, 8));
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, user]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user) return '/auth';
    switch(user.role) {
      case 'patient': return '/dashboard/patient';
      case 'doctor': return '/dashboard/doctor';
      case 'admin': return '/dashboard/admin';
      default: return '/auth';
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(searchResults[0].path);
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const handleResultClick = (path) => {
    navigate(path);
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const isActiveLink = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Colors - keep dark text always since hero has white gradient overlay
  const textColor = 'text-[#2c4667]';
  const hoverColor = 'hover:text-[#2a9fcb]';
  const iconColor = 'text-[#2c4667]';
  const bgNav = isTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-sm';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgNav}`}
        data-testid="main-navbar"
      >
        {/* Single row: Logo center, links below logo, icons right */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center h-20 md:h-36 relative">
            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`absolute left-4 p-2 rounded-full transition-colors md:hidden ${isTransparent ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`}
              data-testid="navbar-mobile-toggle"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${iconColor}`} />
              ) : (
                <Menu className={`w-6 h-6 ${iconColor}`} />
              )}
            </button>

            {/* Center Logo */}
            <Link to="/" data-testid="navbar-logo">
              <img 
                src="/logo-primevision-full.png" 
                alt="PRIME VISION" 
                className="h-8 sm:h-12 md:h-16 w-auto max-w-[180px] sm:max-w-[240px] md:max-w-none object-contain transition-all duration-300"
              />
            </Link>

            {/* Right side icons */}
            <div className="absolute right-4 flex items-center gap-2">
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`p-2 rounded-full transition-colors ${isTransparent ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`}
                data-testid="navbar-search-toggle"
                aria-label="Cerca"
              >
                <Search className={`w-5 h-5 ${iconColor}`} />
              </button>

              {user && <NotificationBell userId={user.id} />}

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className={`flex items-center gap-1 p-2 rounded-full transition-colors ${isTransparent ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`}
                    data-testid="navbar-profile-btn"
                  >
                    <User className={`w-5 h-5 ${iconColor}`} />
                    <span className={`hidden lg:inline text-sm font-medium ${textColor}`}>
                      {user.first_name || 'Profilo'}
                    </span>
                    <ChevronDown className={`w-3 h-3 ${iconColor} transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden">
                      <div className="px-4 py-3 border-b bg-gray-50">
                        <p className="font-semibold text-gray-800">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <div className="py-2">
                        <Link to={getDashboardPath()} onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700">
                          <User className="w-4 h-4" /><span className="text-sm">La mia Dashboard</span>
                        </Link>
                        {user.role === 'patient' && (
                          <Link to="/dashboard/patient?tab=appointments" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700">
                            <Calendar className="w-4 h-4" /><span className="text-sm">Le mie Visite</span>
                          </Link>
                        )}
                        <Link to={getDashboardPath() + '?tab=profile'} onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700">
                          <Settings className="w-4 h-4" /><span className="text-sm">Impostazioni</span>
                        </Link>
                      </div>
                      <div className="border-t">
                        <button onClick={() => { setIsProfileOpen(false); handleLogout(); }} className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 text-red-600 w-full" data-testid="navbar-logout-btn">
                          <LogOut className="w-4 h-4" /><span className="text-sm font-medium">Esci</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/auth" className={`p-2 rounded-full transition-colors ${isTransparent ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`} data-testid="navbar-login-btn">
                  <User className={`w-5 h-5 ${iconColor}`} />
                </Link>
              )}

              {user && (
                <button
                  onClick={() => setIsMessagesOpen(true)}
                  className={`p-2 rounded-full transition-colors ${isTransparent ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`}
                  data-testid="navbar-messages-btn"
                >
                  <Mail className={`w-5 h-5 ${iconColor}`} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Navigation Links (desktop) */}
        <div className="hidden md:block">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-8 lg:gap-10 h-11">
              {NAV_LINKS.map((link) => (
                link.hasDropdown ? (
                  <div
                    key="servizi"
                    className="relative"
                    ref={serviziRef}
                    onMouseEnter={() => {
                      clearTimeout(serviziTimeout.current);
                      setIsServiziOpen(true);
                    }}
                    onMouseLeave={() => {
                      serviziTimeout.current = setTimeout(() => setIsServiziOpen(false), 200);
                    }}
                  >
                    <button
                      className={`flex items-center gap-1 text-sm tracking-widest uppercase font-medium transition-colors ${hoverColor} ${textColor}`}
                      style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.15em' }}
                      data-testid="nav-servizi-trigger"
                    >
                      {link.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isServiziOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mega dropdown */}
                    {isServiziOpen && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-3 w-[780px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[100]" data-testid="servizi-dropdown">
                        <div className="grid grid-cols-3 gap-0">
                          {/* Clinica - colonna 1 */}
                          <div className="p-5 border-r border-gray-100">
                            <h4 className="text-xs tracking-widest uppercase text-[#2A9FCB] font-bold mb-3" style={{ letterSpacing: '0.2em' }}>
                              {SERVIZI_DROPDOWN.clinica.label}
                            </h4>
                            <div className="space-y-1">
                              {SERVIZI_DROPDOWN.clinica.items.slice(0, 9).map(item => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setIsServiziOpen(false)}
                                  className="block px-3 py-2 text-sm text-gray-700 hover:bg-[#E6F4FA] hover:text-[#2A9FCB] rounded-lg transition-colors"
                                  data-testid={`servizi-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                          {/* Clinica - colonna 2 */}
                          <div className="p-5 border-r border-gray-100">
                            <h4 className="text-xs tracking-widest uppercase text-[#2A9FCB] font-bold mb-3 opacity-0" style={{ letterSpacing: '0.2em' }}>
                              .
                            </h4>
                            <div className="space-y-1">
                              {SERVIZI_DROPDOWN.clinica.items.slice(9).map(item => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setIsServiziOpen(false)}
                                  className="block px-3 py-2 text-sm text-gray-700 hover:bg-[#E6F4FA] hover:text-[#2A9FCB] rounded-lg transition-colors"
                                  data-testid={`servizi-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                          {/* Chirurgia */}
                          <div className="p-5">
                            <h4 className="text-xs tracking-widest uppercase text-orange-500 font-bold mb-3" style={{ letterSpacing: '0.2em' }}>
                              {SERVIZI_DROPDOWN.chirurgia.label}
                            </h4>
                            <div className="space-y-1">
                              {SERVIZI_DROPDOWN.chirurgia.items.map(item => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setIsServiziOpen(false)}
                                  className="block px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors"
                                  data-testid={`servizi-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm tracking-widest uppercase font-medium transition-colors ${hoverColor} ${
                      isActiveLink(link.path) ? 'text-[#2a9fcb]' : textColor
                    }`}
                    style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.15em' }}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-100 shadow-lg max-h-[70vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              {NAV_LINKS.map((link) => (
                link.hasDropdown ? (
                  <div key="servizi-mobile">
                    <button
                      onClick={() => setMobileServiziOpen(!mobileServiziOpen)}
                      className="w-full flex items-center justify-between py-3 px-4 text-base font-medium text-[#2c4667] hover:bg-gray-50 rounded-lg"
                      data-testid="mobile-servizi-trigger"
                    >
                      <span>Servizi</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileServiziOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileServiziOpen && (
                      <div className="pl-4 pb-2 space-y-1">
                        <p className="px-4 py-1 text-xs tracking-widest uppercase text-[#2A9FCB] font-bold">Clinica</p>
                        {SERVIZI_DROPDOWN.clinica.items.map(item => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => { setIsMobileMenuOpen(false); setMobileServiziOpen(false); }}
                            className="block py-2 px-4 text-sm text-gray-600 hover:text-[#2A9FCB] hover:bg-blue-50 rounded-lg"
                          >
                            {item.label}
                          </Link>
                        ))}
                        <p className="px-4 py-1 text-xs tracking-widest uppercase text-orange-500 font-bold mt-2">Chirurgia</p>
                        {SERVIZI_DROPDOWN.chirurgia.items.map(item => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => { setIsMobileMenuOpen(false); setMobileServiziOpen(false); }}
                            className="block py-2 px-4 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-3 px-4 text-base font-medium rounded-lg transition-colors ${
                      isActiveLink(link.path) ? 'text-[#2a9fcb] bg-blue-50' : 'text-[#2c4667] hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              {user && (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <Link to={getDashboardPath()} onClick={() => setIsMobileMenuOpen(false)} className="block py-3 px-4 text-base font-medium text-[#2c4667] hover:bg-gray-50 rounded-lg">
                    La Mia Dashboard
                  </Link>
                </>
              )}
              {!user && (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)} className="block py-3 px-4 text-base font-semibold text-[#2a9fcb] hover:bg-blue-50 rounded-lg">
                    Accedi / Registrati
                  </Link>
                </>
              )}
            </div>
          </div>
        )}

        {/* Search Dropdown */}
        {isSearchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 p-4 z-50">
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#2c4667]/60" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca nel sito... (es. cataratta, prenota, strabismo)"
                  className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB]"
                  autoFocus
                  data-testid="search-input"
                />
                <button
                  type="button"
                  onClick={() => { setIsSearchOpen(false); setSearchQuery(''); setSearchResults([]); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </form>
              
              {searchResults.length > 0 && (
                <div className="mt-3 border border-gray-100 rounded-xl overflow-hidden">
                  {searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleResultClick(result.path)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-50 last:border-0"
                      data-testid={`search-result-${idx}`}
                    >
                      <ChevronRight className="w-4 h-4 text-[#2A9FCB]" />
                      <span className="text-[#2c4667] font-medium">{result.title}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="mt-3 text-center text-gray-400 text-sm py-2">
                  Nessun risultato per "{searchQuery}"
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Spacer for fixed navbar - hidden on homepage where navbar overlaps hero */}
      {!isHomePage && <div className="h-44 md:h-60" />}

      {/* Internal Messages Modal */}
      {user && (
        <InternalMessages
          isOpen={isMessagesOpen}
          onClose={() => setIsMessagesOpen(false)}
          currentUser={user}
        />
      )}
    </>
  );
};

export default Navbar;
